// scoring.js — per-pillar scoring with explicit tracker enrollment.
//
// Replaces the composite Life Score. There is no total: each pillar scores
// earned ÷ possible as a percentage, and `possible` counts ENROLLED buckets
// only. A tracker the user has not opted into is `not_tracked`: it contributes
// nothing to earned AND nothing to possible, so it can neither cost points nor
// cap the pillar.
//
// ── why enrollment is explicit ────────────────────────────────────────────────
// The old engine inferred "does this user track meds?" from medications.length.
// That produced an inversion: adding one as-needed medication activated an
// 8-point bucket that only daily-logged meds could fill, dropping the Health
// ceiling from 25/25 to 19/25. Tracking something made the score worse than not
// tracking it. Enrollment is now a stored list (profiles.enabled_trackers), and
// a bucket with nothing scoreable in it reports not_tracked rather than zero.

// Spend exclusion — mirrors api/lib/spend.js and isSpendTx in Scorify.jsx
// (scoring.js cannot import either). Inbound transfers and credit-card/loan
// payments never count; TRANSFER_OUT counts EXCEPT movements between the user's
// own accounts (the detailed set below). Payments to people (Venmo/Zelle/Cash
// App) and cash/checks are spending. Keep all three copies in step.
// ONE spend classification (src/lib/spend.js, 2026-10-17): the copy that used
// to live here is gone, so the score and the resolvers cannot disagree about
// what counts as spending.
import { isExcludedFromSpend, txLocalDay } from './spend.js';

const isSpendExcluded = (t) => isExcludedFromSpend(t);

export const BUCKET_STATE = {
  SCORED: 'scored',           // enrolled and scoreable — counts toward earned AND possible
  NOT_TRACKED: 'not_tracked', // not enrolled, or nothing scoreable — counts toward NEITHER
};

export const PILLARS = [
  { id: 'finance',   label: 'Financial Health', short: 'Money' },
  { id: 'health',    label: 'Health',           short: 'Health' },
  { id: 'habits',    label: 'Habits',           short: 'Habits' },
  { id: 'wellbeing', label: 'Wellbeing',        short: 'Mind' },
];

// `max` is the bucket's weight inside its pillar. Weights are relative, not out
// of 25 — a pillar's percentage is earned ÷ possible over enrolled buckets, so
// the old "must total 25" constraint is gone.
// Money pillar reweighted 2026-10-06 so BEHAVIOR dominates and the savings cushion
// is a small part: a zero cushion (common, and nothing to be ashamed of) no longer
// tanks the score. Behavior = cash flow + budget adherence + bills on time + debt
// (25 of 28), cushion = 3 of 28. See docs/PLAN.md.
export const TRACKERS = {
  spending_discipline: { pillar: 'finance',   label: 'Cash Flow',         max: 7 },                  // income vs spending
  cashflow_trend:      { pillar: 'finance',   label: 'Budget Adherence',  max: 6, plaidOnly: true },// spending held/improved MoM
  bills_timeliness:    { pillar: 'finance',   label: 'Bills On Time',     max: 6 },                  // bills paid for their cycle
  debt_management:     { pillar: 'finance',   label: 'Debt',              max: 6, plaidOnly: true }, // credit utilisation
  savings_cushion:     { pillar: 'finance',   label: 'Savings Cushion',   max: 4 },                  // months of expenses + growth
  activity:            { pillar: 'health',    label: 'Activity & Movement', max: 10 },
  medications:         { pillar: 'health',    label: 'Medications',         max: 4 },
  supplements:         { pillar: 'health',    label: 'Supplements',         max: 4 },
  preventive_care:     { pillar: 'health',    label: 'Preventive Care',     max: 7 },
  habits:              { pillar: 'habits',    label: 'Habit Consistency',   max: 100 },
  mood:                { pillar: 'wellbeing', label: 'Mood Check-ins',      max: 17 },
  journal:             { pillar: 'wellbeing', label: 'Journal',             max: 8 },
};

export const TRACKER_IDS = Object.keys(TRACKERS);

/** green >= 70, amber 40-69, red < 40. null pct has no colour band. */
export function pctBand(pct) {
  if (pct == null) return 'none';
  if (pct >= 70) return 'good';
  if (pct >= 40) return 'fair';
  return 'poor';
}

const ACTIVITY_HABIT_IDS = ['gym', 'steps', 'stretch', 'nojunk', 'cook_home'];
// ── HEALTH PILLAR (rebuilt 2026-10-14) ───────────────────────────────────────
// The old Activity bucket called any day with 5,000+ steps "active" and gave
// 10/10 at five such days, and it was the ONLY Health tracker with data for
// most users, so a user over their screen-time limit with 2 of 4 workouts read
// 100% Health. Now:
//   - Activity grades each day's steps against the user's step goal if they
//     set one, else their own 28-day median (floored), with a workout or a
//     movement-habit log counting as a full day. Days with no data at all are
//     unknown, not zero.
//   - Every active health/fitness goal is a tracker of its own, scored by its
//     resolved progress (the client's getGoalAutoValue): a cap that is over
//     its limit scores low, a count goal scores its share.
//   - Sleep is a tracker when measured nights are synced; absent otherwise.
// Medications, supplements and preventive care stay optional and never cost
// points when off. Goal and sleep trackers are IMPLICIT: they are never in
// profiles.enabled_trackers (that column's CHECK lists TRACKER_IDS only).
export const STEP_TARGET_FLOOR = 3000;       // a very sedentary median never makes every day "full"
export const STEP_TARGET_DEFAULT = 7000;     // no goal and under 7 days of history
export const STEP_HISTORY_DAYS = 28;
export const STEP_HISTORY_MIN_DAYS = 7;
export const SLEEP_TARGET_MIN = 7 * 60;      // 7h asleep = full sleep points
export const SLEEP_MIN_NIGHTS = 3;           // measured nights this week before sleep scores
export const SLEEP_MAX = 6;
export const HEALTH_GOAL_MAX = 6;            // points per health/fitness goal
// Kept for callers that still import them (no longer used by the scorer).
export const ACTIVE_DAY_STEP_FLOOR = 5000;
export const ACTIVITY_TARGET_DAYS = 5;
const AS_NEEDED = /as.?needed|prn|when needed/i;

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const isScheduled = (m) => !AS_NEEDED.test(m?.schedule || '');

/**
 * One-time default enrollment for users whose enabled_trackers is NULL.
 *
 * This is inference, deliberately — but only as a migration default so no
 * existing user wakes up at 0%. Once the user edits their trackers the stored
 * list wins, and inference never runs again. New signups choose explicitly.
 */
export function defaultTrackers({ habits = [], data = {} } = {}) {
  const on = [];
  const plaid = !!data.plaidConnected;
  const expenses = Number(data.effectiveExpenses) || 0;
  const savings = Number(data.savings) || 0;
  const otherAssets = Number(data.otherAssets) || 0;

  if (plaid || savings > 0 || otherAssets > 0) on.push('savings_cushion');
  if (plaid) on.push('debt_management', 'cashflow_trend');
  if (plaid || expenses > 0) on.push('spending_discipline');
  if ((data.bills || []).length > 0) on.push('bills_timeliness');

  if (habits.some(h => ACTIVITY_HABIT_IDS.includes(h.id))
    || (data.workouts || []).length > 0
    || (data.dailyActivity || []).length > 0) on.push('activity');
  // Only scheduled meds imply an intent to track adherence. A user whose only
  // medication is as-needed is NOT enrolled — that is the inversion, fixed.
  if ((data.medications || []).some(isScheduled)) on.push('medications');
  if ((data.supplements || []).length > 0) on.push('supplements');
  if ((data.checkups || []).length > 0) on.push('preventive_care');

  if (habits.length > 0) on.push('habits');
  if ((data.moodHistory || []).length > 0) on.push('mood');
  if ((data.journalEntries || []).length > 0) on.push('journal');

  return on;
}

/** Normalise whatever came back from the database into a Set of valid ids. */
export function resolveEnrolled(stored, fallbackCtx) {
  if (Array.isArray(stored)) return new Set(stored.filter(id => TRACKERS[id]));
  return new Set(defaultTrackers(fallbackCtx));
}

const untracked = (id, detail) => ({
  id, pillar: TRACKERS[id].pillar, label: TRACKERS[id].label, max: TRACKERS[id].max,
  earned: 0, state: BUCKET_STATE.NOT_TRACKED, detail,
});
const scored = (id, earned, detail) => ({
  id, pillar: TRACKERS[id].pillar, label: TRACKERS[id].label, max: TRACKERS[id].max,
  earned: clamp(earned, 0, TRACKERS[id].max), state: BUCKET_STATE.SCORED, detail,
});
// Implicit trackers (goals, sleep): not in TRACKERS, so they carry their own
// pillar / label / max. `implicit` tells the UI there is nothing to toggle.
const implicitUntracked = (id, label, max, detail) => ({ id, pillar: 'health', label, max, earned: 0, state: BUCKET_STATE.NOT_TRACKED, detail, implicit: true });
const implicitScored = (id, label, max, earned, detail) => ({ id, pillar: 'health', label, max, earned: clamp(earned, 0, max), state: BUCKET_STATE.SCORED, detail, implicit: true });
const fmtMin = (m) => { const h = Math.floor(m / 60), mm = Math.round(m % 60); return h ? (mm ? `${h}h ${mm}m` : `${h}h`) : `${mm}m`; };

// ── individual buckets ────────────────────────────────────────────────────────
// Each returns a bucket object. Returning NOT_TRACKED from inside a scorer means
// "enrolled, but there is genuinely nothing here to score" — which must not be
// rendered as a zero, because a zero reads as failure.

// The cushion balance: Plaid savings-type accounts PLUS manually entered savings
// (finances.savings) PLUS "Other savings and assets" (gold/silver, cash, other
// bank/brokerage, other — d.otherAssets). Plaid CHECKING is never counted (it is not
// a cushion). All real stores add together — someone with a Plaid checking and manual
// gold should get credit for the gold.
export function cushionBalance(d) {
  const plaid = !!d.plaidConnected;
  const accounts = d.plaidAccounts || [];
  const dedicated = plaid ? accounts
    .filter(a => a.type === 'depository' && ['savings', 'money market', 'cd', 'prepaid', 'payroll'].includes(a.subtype))
    .reduce((a, acc) => a + (acc.balances?.current || 0), 0) : 0;
  const otherDep = plaid && dedicated === 0 ? accounts
    .filter(a => a.type === 'depository' && a.subtype !== 'checking')
    .reduce((a, acc) => a + (acc.balances?.current || 0), 0) : 0;
  const plaidSavings = dedicated > 0 ? dedicated : otherDep;
  const manual = (Number(d.savings) || 0) + (Number(d.otherAssets) || 0);
  return plaidSavings + manual;
}

// Savings cushion, measured in MONTHS of average monthly expenses (not a flat dollar
// amount), capped small (max 3) so it cannot dominate. A zero cushion scores 0 but is
// NOT a failure state in copy. Progress credit (item 4): month-over-month GROWTH earns
// points even from a zero start, so starting to save is rewarded before a full month
// of runway exists. earned = the better of the level and the growth credit.
function scoreSavingsCushion(d, enrolled) {
  if (!enrolled.has('savings_cushion')) return untracked('savings_cushion', 'Not tracked');
  const balance = cushionBalance(d);
  const expenses = Number(d.effectiveExpenses) || 0;
  if (expenses <= 0) return untracked('savings_cushion', 'Add monthly expenses to score this');
  const M = TRACKERS.savings_cushion.max; // 4
  const months = balance / expenses;
  const level = months >= 3 ? M : months >= 2 ? 3 : months >= 1 ? 2 : months >= 0.5 ? 1 : months > 0 ? 0.5 : 0;

  // Growth credit: compare to last month's cushion balance (d.prevSavingsBalance,
  // supplied from the monthly snapshot). Any real increase earns credit — a meaningful
  // monthly gain (>= a quarter-month of expenses) earns more. Rewards a zero start.
  const prev = d.prevSavingsBalance;
  let growth = 0;
  const grew = typeof prev === 'number' && isFinite(prev) && balance > prev;
  if (grew) growth = (balance - prev) >= expenses * 0.25 ? 2 : 1;

  const earned = Math.min(M, Math.max(level, growth));
  if (balance <= 0 && !grew) {
    return scored('savings_cushion', 0, 'No cushion yet — even a little saved this month earns credit');
  }
  const monthsLabel = `${months.toFixed(1)} mo of expenses`;
  const detail = grew ? `${monthsLabel} · growing this month` : monthsLabel;
  return scored('savings_cushion', earned, detail);
}

// Bills paid on time: of the user's bills, the share in GOOD STANDING for this cycle.
// A bill counts against only when it is NOT autopay, NOT paid for the current cycle,
// AND its due day has already passed this month (genuinely late). Autopay bills, bills
// paid for this cycle, and bills not yet due all count as good standing. No bills on
// file => not tracked (never a 0). Manual, so it works without Plaid.
function scoreBillsTimeliness(d, enrolled) {
  if (!enrolled.has('bills_timeliness')) return untracked('bills_timeliness', 'Not tracked');
  const bills = (d.bills || []).filter(b => Number.isInteger(Number(b.due_day)) && Number(b.due_day) >= 1 && Number(b.due_day) <= 31);
  if (bills.length === 0) return untracked('bills_timeliness', 'No bills added');
  const todayStr = d.todayStr || '';
  const currentMonth = todayStr.slice(0, 7);
  const todayDay = Number(todayStr.slice(8, 10)) || 0;
  const pastDueUnpaid = bills.filter(b =>
    b.autopay !== true &&
    b.paid_for_month !== currentMonth &&
    Number(b.due_day) <= todayDay
  );
  const good = bills.length - pastDueUnpaid.length;
  const pts = (good / bills.length) * TRACKERS.bills_timeliness.max;
  const detail = pastDueUnpaid.length === 0
    ? `${bills.length} bill${bills.length === 1 ? '' : 's'} on track`
    : `${pastDueUnpaid.length} past due, ${good}/${bills.length} on track`;
  return scored('bills_timeliness', pts, detail);
}

function scoreDebtManagement(d, enrolled) {
  if (!enrolled.has('debt_management')) return untracked('debt_management', 'Not tracked');
  if (!d.plaidConnected) return untracked('debt_management', 'Connect a bank to score this');
  const credit = (d.plaidAccounts || []).filter(a => a.type === 'credit');
  const limit = credit.reduce((a, acc) => a + (acc.balances?.limit || 0), 0);
  if (credit.length === 0 || limit <= 0) return untracked('debt_management', 'No credit accounts found');
  const bal = credit.reduce((a, acc) => a + (acc.balances?.current || 0), 0);
  const util = bal / limit;
  const pts = util <= 0.1 ? 6 : util <= 0.2 ? 5 : util <= 0.3 ? 4 : util <= 0.5 ? 2.5 : util <= 0.75 ? 1 : 0;
  return scored('debt_management', pts, `${Math.round(util * 100)}% credit utilisation`);
}

// Cash Flow — are you spending less than you earn? The core money behavior, so it is
// the heaviest finance bucket (max 7). Works with manual or Plaid-derived figures.
function scoreSpendingDiscipline(d, enrolled) {
  if (!enrolled.has('spending_discipline')) return untracked('spending_discipline', 'Not tracked');
  const spend = Number(d.effectiveExpenses) || 0;   // manual or Plaid-derived
  const income = Number(d.income) || 0;
  if (spend <= 0) return untracked('spending_discipline', 'Add monthly expenses to score this');
  if (income <= 0) return scored('spending_discipline', 3, `$${Math.round(spend).toLocaleString()} spent — add income for full score`);
  const ratio = spend / income;
  const pts = ratio <= 0.6 ? 7 : ratio <= 0.75 ? 5.5 : ratio <= 0.9 ? 4 : ratio <= 1.0 ? 2 : 0;
  return scored('spending_discipline', pts, `spending ${Math.round(ratio * 100)}% of income`);
}

// 'YYYY-MM' for the calendar month `back` months before the local month in todayStr.
function monthKeyBack(todayStr, back) {
  const [y, m] = (todayStr || '').split('-').map(Number);
  if (!y || !m) return null;
  const idx = (y * 12 + (m - 1)) - back;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

// Per-budget adherence over the last complete month: 1.0 within limit, partial for a
// small overage, 0 for a large one. A monthly budget scored on the current (partial)
// month would always look "within" early in the month, so the last COMPLETE month is
// used (same reason Budget Adherence's fallback compares complete months).
function budgetFraction(ratio) {
  if (ratio <= 1.0) return 1;        // within budget
  if (ratio <= 1.1) return 0.7;      // up to 10% over
  if (ratio <= 1.25) return 0.4;     // up to 25% over
  if (ratio <= 1.5) return 0.15;     // up to 50% over
  return 0;                          // blew it
}

// Spend in `monthKey` for one budget's categories (detailed set if given, else primary).
function budgetSpend(txns, monthKey, budget, zone) {
  const det = new Set(budget.detailed || []);
  return txns
    .filter(t => txLocalDay(t, zone)?.startsWith(monthKey) && t.amount > 0 && !isSpendExcluded(t))
    .filter(t => {
      const dd = t.personal_finance_category?.detailed || '';
      const pp = t.personal_finance_category?.primary || t.category?.[0] || '';
      return det.size ? det.has(dd) : (!!budget.primary && pp === budget.primary);
    })
    .reduce((a, t) => a + t.amount, 0);
}

// Budget Adherence. When the user has set category budgets (monthly spend-cap goals —
// d.budgets), score the SHARE of budgeted categories kept within limit last month, with
// partial credit for small overages. This fixes the gap where a user $85 over a $400
// eating-out budget still scored full marks because month-over-month net flow held.
// With NO budgets set, fall back to the month-over-month surplus comparison.
function scoreCashflowTrend(d, enrolled) {
  if (!enrolled.has('cashflow_trend')) return untracked('cashflow_trend', 'Not tracked');
  if (!d.plaidConnected) return untracked('cashflow_trend', 'Connect a bank to score this');
  const txns = d.plaidTransactions || [];
  const M = TRACKERS.cashflow_trend.max;
  const recentM = monthKeyBack(d.todayStr, 1);   // last complete month
  const priorM = monthKeyBack(d.todayStr, 2);     // the one before it

  // ADHERENCE (2026-10-17, src/lib/goalAdherence.js budgetAdherence): half the
  // share of the last two complete months within each cap, half this month's
  // pace, periods only from the goal's creation on. The client and the server
  // pass the same record; the legacy last-month rule below stays as fallback.
  if (d.budgetAdherence) {
    const a = d.budgetAdherence;
    if (a.hasData === false) return untracked('cashflow_trend', a.detail || 'Needs bank history');
    return scored('cashflow_trend', clamp(Number(a.score01) || 0, 0, 1) * M, a.detail || '');
  }
  const budgets = (d.budgets || []).filter(b => Number(b.limit) > 0 && (b.primary || (Array.isArray(b.detailed) && b.detailed.length)));
  if (budgets.length > 0) {
    if (!recentM || !txns.some(t => txLocalDay(t, d.zone)?.startsWith(recentM))) {
      return untracked('cashflow_trend', 'Needs a full month of history');
    }
    let sum = 0, over = 0;
    for (const b of budgets) {
      const actual = budgetSpend(txns, recentM, b, d.zone);
      const ratio = actual / Number(b.limit);
      sum += budgetFraction(ratio);
      if (ratio > 1) over++;
    }
    const pts = (sum / budgets.length) * M;
    const within = budgets.length - over;
    const detail = over === 0
      ? `all ${budgets.length} budget${budgets.length === 1 ? '' : 's'} within limit last month`
      : `${within}/${budgets.length} budget${budgets.length === 1 ? '' : 's'} within limit last month`;
    return scored('cashflow_trend', pts, detail);
  }

  // Fallback: month-over-month net flow (two most recent complete months).
  if (!recentM || !priorM || !txns.some(t => txLocalDay(t, d.zone)?.startsWith(priorM))) {
    return untracked('cashflow_trend', 'Needs two full months of history');
  }
  const flow = (ms) => {
    const rows = txns.filter(t => txLocalDay(t, d.zone)?.startsWith(ms));
    const inc = rows.filter(t => t.amount < 0).reduce((a, t) => a + Math.abs(t.amount), 0);
    const exp = rows.filter(t => t.amount > 0 && !isSpendExcluded(t)).reduce((a, t) => a + t.amount, 0);
    return inc - exp;
  };
  const cur = flow(recentM), prev = flow(priorM);
  const pts = cur > 0 && prev > 0 && cur >= prev ? M
    : cur > 0 && prev > 0 ? M - 1
    : cur > 0 ? M - 2
    : cur >= prev ? 1 : 0;
  const sign = cur >= 0 ? '+' : '-';
  return scored('cashflow_trend', pts, `${sign}$${Math.round(Math.abs(cur)).toLocaleString()} last month`);
}

// ── HABIT CONSISTENCY (rewritten 2026-10-13) ─────────────────────────────────
// The old ratio was streak × scorePerStreak ÷ 10: a streak gauge, not a
// consistency score. One missed day zeroed every habit the next time the app
// opened (a Tuesday afternoon read 0% while the cards showed 36% and 50%
// 28-day rates), and a "3 times a week" habit kept perfectly still lost its
// streak on every rest day. Streaks are display-only now, the way the
// activity tracker already treats them.
//
// New basis: logs in the last 28 days against the habit's OWN target over
// those 28 days. Weekly targets ("5 times/week", "7 days/week") × 4 weeks;
// monthly targets ("4 times/month", "1 per month") count once, 28 days being
// one month for this purpose. Capped at 1 so extra logs never hide a missed
// habit elsewhere. The habit card's "28-day rate" and the Habits breakdown
// read these same helpers, so the three numbers cannot disagree.
export const HABIT_WINDOW_DAYS = 28;

const habitTemplate = (h, templates) => (templates || []).find(x => x.id === h.id) || null;

/** Expected logs over the 28-day window from the habit's target + unit. The
 *  habit ROW wins (an edited target is saved there, for template and custom
 *  habits alike); the template is the fallback for rows without one, and a
 *  habit with neither resolves to 5 times/week. Same precedence as tmpl() in
 *  Scorify.jsx and the server's habit shape (kaiContext.normalizeServerBundle). */
export function habitTarget28(h, templates) {
  const t = habitTemplate(h, templates);
  const target = Number(h?.target ?? t?.target ?? 5) || 5;
  const unit = String(h?.unit ?? t?.unit ?? 'times/week');
  return /month/i.test(unit) ? target : target * 4;
}

/** 'YYYY-MM-DD' of the first day in the window ending on todayStr (28 days inclusive). */
export function habitWindowStart(todayStr) {
  const base = todayStr ? new Date(`${todayStr}T00:00:00`) : new Date();
  base.setDate(base.getDate() - (HABIT_WINDOW_DAYS - 1));
  const y = base.getFullYear(), m = String(base.getMonth() + 1).padStart(2, '0'), d = String(base.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Distinct logged dates inside the window (completions are local date strings). */
export function habitLogs28(h, todayStr) {
  const start = habitWindowStart(todayStr);
  const end = todayStr || '9999-12-31';
  return new Set((h?.completions || []).filter(ds => typeof ds === 'string' && ds >= start && ds <= end)).size;
}

/** Per-habit consistency as a 0..1 ratio: logs ÷ target over the last 28 days. */
export function habitRatio(h, templates, todayStr) {
  const need = habitTarget28(h, templates);
  if (need <= 0) return 0;
  return clamp(habitLogs28(h, todayStr) / need, 0, 1);
}

// Active days over the last 7, not streaks. The streak-based version zeroed
// the whole bucket two mornings after one missed manual log — while Apple
// Health showed 7/7 active days it never read. Streaks remain display-only.
// A day is active (counted once, whatever the source) if it has:
//   - a movement-habit completion (h.completions date strings), or
//   - a workout row (any source — manual or healthkit), or
//   - a daily_activity row with steps >= ACTIVE_DAY_STEP_FLOOR.
// HealthKit rule: denied/revoked access yields empty results, never an error,
// so a missing source simply contributes no days. But when passive data is the
// ONLY source and the window is empty, that silence is indistinguishable from
// revoked permissions (coach.js hasFitnessData precedent) — report not_tracked,
// never a 0 that reads as "you didn't move".
/** The daily step target: the user's step goal if set, else their own
 *  28-day median (floored), else a plain default. */
export function stepTarget({ stepGoal = null, dailyActivity = [], todayStr } = {}) {
  if (Number(stepGoal) > 0) return { target: Number(stepGoal), basis: 'goal' };
  const prior = (dailyActivity || [])
    .filter(r => r && r.date && (!todayStr || String(r.date).slice(0, 10) < todayStr) && Number(r.steps) > 0)
    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
    .slice(0, STEP_HISTORY_DAYS)
    .map(r => Number(r.steps));
  if (prior.length >= STEP_HISTORY_MIN_DAYS) {
    const sorted = [...prior].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const med = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    return { target: Math.max(STEP_TARGET_FLOOR, Math.round(med)), basis: 'usual' };
  }
  return { target: STEP_TARGET_DEFAULT, basis: 'default' };
}

function scoreActivity(habits, d, enrolled) {
  if (!enrolled.has('activity')) return untracked('activity', 'Not tracked');
  const act = habits.filter(h => ACTIVITY_HABIT_IDS.includes(h.id));
  const workouts = d.workouts || [];
  const daily = d.dailyActivity || [];
  if (act.length === 0 && workouts.length === 0 && daily.length === 0) {
    return untracked('activity', 'No movement habits or fitness data yet');
  }
  const week = new Set(last7(d.todayStr));
  // Steps per day this week (summed if a day has several rows), and the days
  // with a workout row or a movement-habit log (a full day either way).
  const stepsByDay = new Map();
  for (const row of daily) {
    const ds = (row.date || '').slice(0, 10);
    if (!week.has(ds)) continue;
    stepsByDay.set(ds, (stepsByDay.get(ds) || 0) + (Number(row.steps) || 0));
  }
  const movementDays = new Set();
  for (const h of act) for (const ds of h.completions || []) if (week.has(ds)) movementDays.add(ds);
  for (const w of workouts) { const ds = (w.date || '').slice(0, 10); if (week.has(ds)) movementDays.add(ds); }
  const known = new Set([...stepsByDay.keys(), ...movementDays]);
  // Passive-source silence must not read as inactivity (HealthKit returns
  // nothing on denial, never an error). A user who chose manual habits gets an
  // honest 0 for an empty week instead.
  if (act.length === 0 && known.size === 0) return untracked('activity', 'No activity data this week');
  if (known.size === 0) return scored('activity', 0, '0/7 days with activity this week');
  const { target, basis } = stepTarget({ stepGoal: d.stepGoal, dailyActivity: daily, todayStr: d.todayStr });
  let sum = 0;
  for (const ds of known) {
    let sc = stepsByDay.has(ds) ? Math.min(stepsByDay.get(ds) / target, 1) : 0;
    if (movementDays.has(ds)) sc = 1;
    sum += sc;
  }
  const pts = (sum / known.size) * 10;
  const avgSteps = stepsByDay.size ? Math.round([...stepsByDay.values()].reduce((a, b) => a + b, 0) / stepsByDay.size) : null;
  const vs = basis === 'goal' ? `your ${target.toLocaleString()}-step goal` : basis === 'usual' ? `your usual ${target.toLocaleString()}` : `a ${target.toLocaleString()}-step baseline`;
  const detail = `${avgSteps != null ? `${avgSteps.toLocaleString()} steps/day` : 'no step data'} vs ${vs}${movementDays.size ? ` · ${movementDays.size} workout day${movementDays.size === 1 ? '' : 's'}` : ''} · ${known.size}/7 days with data`;
  return scored('activity', pts, detail);
}

// SLEEP (implicit): measured nights this week (asleep_min set; in-bed-only
// rows never count), average against 7h. Absent, not zero, when unsynced.
function scoreSleep(d) {
  const week = new Set(last7(d.todayStr));
  const nights = (d.sleepNights || []).filter(n => n && n.asleep_min != null && week.has(String(n.date || '').slice(0, 10)));
  if (nights.length < SLEEP_MIN_NIGHTS) {
    return implicitUntracked('sleep', 'Sleep', SLEEP_MAX, nights.length ? `Only ${nights.length} night${nights.length === 1 ? '' : 's'} measured this week` : 'No sleep data synced');
  }
  const avg = nights.reduce((a, n) => a + Number(n.asleep_min), 0) / nights.length;
  return implicitScored('sleep', 'Sleep', SLEEP_MAX, (avg / SLEEP_TARGET_MIN) * SLEEP_MAX, `avg ${fmtMin(avg)} over ${nights.length} nights (7h is full marks)`);
}

// HEALTH GOALS (implicit, one tracker per goal): resolved progress in, points
// out. A cap (inverted) is full at or under its limit, then falls to 0 at 50%
// over; a count goal scores its share of the target.
function scoreHealthGoals(d) {
  const out = [];
  for (const g of d.healthGoals || []) {
    if (!g || !g.id) continue;
    const id = `goal:${g.id}`;
    const label = String(g.title || 'Goal');
    const target = Number(g.target) || 0;
    // ADHERENCE (2026-10-17, src/lib/goalAdherence.js): a cap or count goal
    // with a history record is scored on that history (days under the cap,
    // weeks on pace), never on today alone. Manual targets keep the ratio.
    if (g.adherence) {
      const a = g.adherence;
      if (a.hasData === false) { out.push(implicitUntracked(id, label, HEALTH_GOAL_MAX, a.detail || 'not enough history yet')); continue; }
      out.push(implicitScored(id, label, HEALTH_GOAL_MAX, clamp(Number(a.score01) || 0, 0, 1) * HEALTH_GOAL_MAX, a.detail || ''));
      continue;
    }
    if (g.hasData === false) { out.push(implicitUntracked(id, label, HEALTH_GOAL_MAX, 'progress unavailable right now')); continue; }
    if (target <= 0) { out.push(implicitUntracked(id, label, HEALTH_GOAL_MAX, 'no target set')); continue; }
    const value = Number(g.value) || 0;
    const ratio = value / target;
    const unit = String(g.unit || '');
    const fmtV = (v) => unit === 'min' ? fmtMin(v) : unit === '$' ? `$${Math.round(v).toLocaleString()}` : `${Math.round(v * 10) / 10}${unit ? ` ${unit}` : ''}`;
    const per = g.period === 'day' ? 'today' : g.period === 'week' ? 'this week' : g.period === 'month' ? 'this month' : '';
    if (g.inverted) {
      const pts = ratio <= 1 ? HEALTH_GOAL_MAX : HEALTH_GOAL_MAX * clamp(1 - (ratio - 1) / 0.5, 0, 1);
      const overPct = Math.round((ratio - 1) * 100);
      out.push(implicitScored(id, label, HEALTH_GOAL_MAX, pts, `${fmtV(value)} of ${fmtV(target)} limit${per ? ` ${per}` : ''}${ratio > 1 ? ` (over by ${overPct}%)` : ''}`));
    } else {
      out.push(implicitScored(id, label, HEALTH_GOAL_MAX, Math.min(ratio, 1) * HEALTH_GOAL_MAX, `${fmtV(value)} of ${fmtV(target)}${per ? ` ${per}` : ''}`));
    }
  }
  return out;
}

function scoreMedications(d, enrolled) {
  if (!enrolled.has('medications')) return untracked('medications', 'Not tracked');
  const meds = (d.medications || []).filter(isScheduled);
  // As-needed-only is the inversion case: there is no daily adherence to score,
  // so it reports not_tracked instead of dragging the pillar down.
  if (meds.length === 0) return untracked('medications', 'No scheduled medications');
  const taken = meds.filter(m => m.last_taken_date === d.todayStr).length;
  const lowRefill = Number(d.lowRefillMeds) || 0;
  const pts = (taken / meds.length) * 4 - (lowRefill > 0 ? 1 : 0);
  return scored('medications', pts, `${taken}/${meds.length} taken today${lowRefill > 0 ? ' · refill due' : ''}`);
}

function scoreSupplements(d, enrolled) {
  if (!enrolled.has('supplements')) return untracked('supplements', 'Not tracked');
  const supps = d.supplements || [];
  if (supps.length === 0) return untracked('supplements', 'No supplements added');
  const avg = supps.reduce((a, s) => a + (s.streak || 0), 0) / supps.length;
  return scored('supplements', clamp((avg / 14) * 4, 0, 4), `${Math.round(avg)}-day average streak`);
}

function scorePreventiveCare(d, enrolled) {
  if (!enrolled.has('preventive_care')) return untracked('preventive_care', 'Not tracked');
  const checks = d.checkups || [];
  if (checks.length === 0) return untracked('preventive_care', 'No check-ups added');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const future = (c) => c.next_date && new Date(c.next_date) > today;
  const overdue = checks.filter(c => c.urgent && !future(c)).length;
  const upcoming = checks.filter(future).length;
  const pts = clamp(7 - overdue * 2 + Math.min(2, upcoming), 0, 7);
  return scored('preventive_care', pts, overdue > 0 ? `${overdue} overdue` : `${checks.length} up to date`);
}

function scoreHabits(habits, enrolled, templates, todayStr) {
  if (!enrolled.has('habits')) return untracked('habits', 'Not tracked');
  if (habits.length === 0) return untracked('habits', 'No habits added');
  // Mean of per-habit consistency (logs ÷ own target, last 28 days), so the
  // score never depends on how many habits exist.
  const mean = habits.reduce((a, h) => a + habitRatio(h, templates, todayStr), 0) / habits.length;
  return scored('habits', mean * 100, `${habits.length} habit${habits.length === 1 ? '' : 's'} · ${Math.round(mean * 100)}% average, last ${HABIT_WINDOW_DAYS} days`);
}

function last7(todayStr) {
  const base = todayStr ? new Date(`${todayStr}T00:00:00`) : new Date();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base); d.setDate(d.getDate() - i);
    return d.toISOString().split('T')[0];
  });
}

function scoreMood(d, enrolled) {
  if (!enrolled.has('mood')) return untracked('mood', 'Not tracked');
  const history = d.moodHistory || [];
  // NOT STARTED (policy 2026-09-28): onboarding auto-enrolls mood, so every
  // fresh account showed Mind at a red 0% before the first check-in ever
  // existed. Enrolled with ZERO valid entries ever = not started — excluded
  // from scoring like the other "enrolled but no data source yet" buckets
  // (activity/meds/supplements), so the pillar reads "—", never 0%. The
  // FIRST check-in flips it to normal scoring, and from then on missed
  // weeks score 0 exactly as before.
  if (!history.some(m => m.score !== null && m.score !== undefined)) {
    return untracked('mood', 'No check-ins yet');
  }
  const week = last7(d.todayStr);
  const days = week.filter(ds => history.some(m => m.date === ds && m.score !== null)).length;
  const checkIn = [0, 1, 2, 4, 6, 8, 10, 12][Math.min(days, 7)];
  let streak = 0;
  for (let i = 0; i < 90; i++) {
    const dt = new Date(); dt.setDate(dt.getDate() - i);
    const ds = dt.toISOString().split('T')[0];
    if (history.some(m => m.date === ds && m.score !== null)) streak++; else break;
  }
  const streakPts = streak >= 30 ? 5 : streak >= 14 ? 4 : streak >= 7 ? 3 : streak >= 3 ? 2 : streak >= 1 ? 1 : 0;
  return scored('mood', checkIn + streakPts, `${days}/7 days this week · ${streak}-day streak`);
}

function scoreJournal(d, enrolled) {
  if (!enrolled.has('journal')) return untracked('journal', 'Not tracked');
  const entries = d.journalEntries || [];
  // Same not-started rule as scoreMood above: zero entries ever = "—",
  // first entry starts normal scoring, lapsed weeks score 0 after that.
  if (entries.length === 0) {
    return untracked('journal', 'No entries yet');
  }
  const week = last7(d.todayStr);
  const n = week.filter(ds => entries.some(j => (j.date || (j.created_at || '').slice(0, 10)) === ds)).length;
  const pts = n >= 4 ? 8 : n === 3 ? 6 : n === 2 ? 4 : n === 1 ? 2 : 0;
  return scored('journal', pts, `${n} ${n === 1 ? 'entry' : 'entries'} this week`);
}

/**
 * Score every pillar.
 *
 * Returns { pillars: [{ id, label, short, earned, possible, pct, band,
 *                       trackedCount, totalCount, buckets: [...] }] }
 *
 * `pct` is null when a pillar has no enrolled, scoreable bucket — the caller
 * renders that as "—", never as 0%. There is deliberately no total.
 */
export function calcPillarScores({ habits = [], data = {}, enrolled, templates = [] } = {}) {
  const set = enrolled instanceof Set ? enrolled : resolveEnrolled(enrolled, { habits, data });

  const buckets = [
    scoreSavingsCushion(data, set),
    scoreDebtManagement(data, set),
    scoreSpendingDiscipline(data, set),
    scoreCashflowTrend(data, set),
    scoreBillsTimeliness(data, set),
    scoreActivity(habits, data, set),
    ...scoreHealthGoals(data),
    scoreSleep(data),
    scoreMedications(data, set),
    scoreSupplements(data, set),
    scorePreventiveCare(data, set),
    scoreHabits(habits, set, templates, data.todayStr),
    scoreMood(data, set),
    scoreJournal(data, set),
  ];

  const pillars = PILLARS.map(p => {
    const own = buckets.filter(b => (b.pillar || TRACKERS[b.id]?.pillar) === p.id);
    const live = own.filter(b => b.state === BUCKET_STATE.SCORED);
    const earned = live.reduce((a, b) => a + b.earned, 0);
    const possible = live.reduce((a, b) => a + b.max, 0);
    const pct = possible > 0 ? Math.round((earned / possible) * 100) : null;
    return {
      id: p.id, label: p.label, short: p.short,
      earned: Math.round(earned * 10) / 10,
      possible,
      pct,
      band: pctBand(pct),
      trackedCount: live.length,
      totalCount: own.length,
      buckets: own,
    };
  });

  return { pillars };
}
